import * as A from 'astronomy-engine';
import type { Place } from '../astro/frame';
import { daysBetween, isDecided, monthStart, nextMonth, sunsetOn } from '../astro/hijri';
import { dayMonth } from '../lib/hijriAlerts';
import { addDays, clockAt, wallTime } from '../lib/localtime';
import { crescentDay, hijriNow, lookingFor, lookingText, saudiStarts } from '../live/saudi';
import { MoonGlyph, crescentPhase } from './MoonGlyph';

// Ramaḍān in Tonight (BUILD-SPEC item 42): its start as Saudi Arabia announced it, each night's Moon, and the last ten
// nights named as Islamic nights, each beginning at sunset. No times at all, and nothing ever says which night is
// Laylat al-Qadr. The crescent of Shawwāl is looked for on the evening of the 29th, as for every month (item 19).

/** "21st". */
const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')}`;

export function RamadanView({ now, pl }: { now: number; pl: Place }) {
  const starts = saudiStarts.value;
  const h = hijriNow(now, pl, starts);
  if (h.month !== 9) return null;
  const month = { month: 9, year: h.year };
  const start = monthStart(month, starts);
  const shawwal = nextMonth(month);
  // Thirty nights only once Saudi Arabia has said the month completes thirty days.
  const length = isDecided(shawwal, starts) ? daysBetween(start, monthStart(shawwal, starts)) : 29;
  const tz = pl.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const w = { lat: pl.lat, lon: pl.lon, tz };
  // The night of the nth begins at the sunset that ends the day before the nth.
  const nightStart = (n: number) => sunsetOn(addDays(start, n - 2), w);
  // Before sunrise, the night under way is that of today's day of Ramaḍān; from sunrise, the night that begins at
  // today's sunset (the next day's). Where the Sun does not rise today (polar night or day), the night under way lasts
  // until noon, as in Tonight (observingNight, src/astro/solar.ts); the date itself still turns at 18:00 (sunsetOn).
  // Counted from the date, not from the Hijri day, which on the evening of the 29th waits for Saudi Arabia's
  // announcement although the night of the 29th has gone.
  const today = clockAt(now, tz).date;
  const obs = new A.Observer(pl.lat, pl.lon, 0), midnight = new Date(wallTime(today, tz, 0));
  const morning = (A.SearchRiseSet(A.Body.Sun, obs, 1, midnight, 1) ?? A.SearchHourAngle(A.Body.Sun, obs, 0, midnight).time).date.getTime();
  const day = daysBetween(start, today) + 1;
  const tonight = now < morning ? day : day + 1;
  const looking = lookingFor(now, pl, starts);
  const nights = Array.from({ length }, (_, i) => i + 1);
  const announced = isDecided(month, starts) ? ', as Saudi Arabia announced' : '';
  return (
    <section class="card wide ramadan" aria-label={`Ramaḍān ${h.year}`}>
      <div class="card-title">Ramaḍān {h.year}</div>
      {/* On the night of the 1st, its day is still to come. */}
      <p class="ramadan-line">{today < start ? `The 1st of Ramaḍān is ${dayMonth(start)}${announced}.` : `Ramaḍān began on ${dayMonth(start)}${announced}.`}</p>
      <p class="ramadan-line">{looking ? lookingText(looking) : tonight <= length ? `Tonight is the night of the ${ordinal(tonight)}.` : ''}</p>
      <ol class="ramadan-nights" aria-label="Each night's Moon">
        {nights.map((n) => {
          const t = nightStart(n);
          const real = A.MoonPhase(new Date(t + 2 * 3600e3));
          const phase = crescentDay(t + 60e3, pl, starts) ? crescentPhase(real) : real;
          return (
            <li key={n} class={`${n === tonight ? 'on' : ''} ${n < tonight ? 'past' : ''} ${n >= 21 ? 'last-ten' : ''}`} aria-current={n === tonight ? 'date' : undefined} aria-label={`The night of the ${ordinal(n)}${n === tonight ? ', tonight' : ''}`}>
              <MoonGlyph phase={phase} size={22} />
              <span class="tnum">{n}</span>
            </li>
          );
        })}
      </ol>
      <div class="ramadan-ten">
        <div class="ramadan-ten-title">The last ten nights</div>
        <ul>
          {nights.filter((n) => n >= 21).map((n) => (
            <li key={n} class={n < tonight ? 'past' : ''}>The night of the {ordinal(n)} {n < tonight ? 'began' : 'begins'} at sunset on {dayMonth(addDays(start, n - 2))}.</li>
          ))}
        </ul>
        {!isDecided(shawwal, starts) && !looking && <p class="ramadan-line">On the evening of the 29th, {dayMonth(addDays(start, 28))}, Saudi Arabia looks for the crescent of Shawwāl.</p>}
      </div>
    </section>
  );
}
